import {
  useCallback,
  useEffect,
  useRef,
  useState,
  ReactNode,
  MouseEvent,
} from 'react'
import helpCircle from '@/assets/imgs/help-circle.svg'
import { GENERICS as dict } from '@/resources/dict'

import * as S from './styles'

interface TooltipProps {
  children: ReactNode
  target?: ReactNode
  placement: string
  id: string
  iconSize?: number
  contentSize?: 'small' | 'normal'
  dark?: boolean
  disabled?: boolean
  helpCircleIconImg?: string
  onClick?: (event?: MouseEvent) => void
  onMouseOver?: () => void
  onTooltipActive?: () => void
}

const Tooltip = ({
  children,
  target,
  placement,
  onMouseOver,
  id,
  iconSize,
  contentSize,
  onTooltipActive,
  dark,
  disabled,
  onClick,
  helpCircleIconImg = helpCircle,
  ...props
}: TooltipProps) => {
  const [active, setActive] = useState(false)
  const [targetSize, setTargetSize] = useState(0)
  const containerEl = useRef<HTMLDivElement>(null)

  const onMouseOverHandle = () => {
    if (disabled) {
      return
    }

    if (!active) {
      setActive(true)
      onTooltipActive?.()
    }

    onMouseOver?.()
  }

  const onMouseLeaveHandle = () => setActive(false)

  const targetSizeHandle = useCallback(() => {
    const currentWidth = containerEl.current
      ? containerEl.current.offsetWidth
      : 0

    if (targetSize === currentWidth) {
      return
    }

    setTargetSize(currentWidth)
  }, [targetSize])

  useEffect(() => {
    if (containerEl.current) {
      targetSizeHandle()

      window.addEventListener('resize', targetSizeHandle)
    }

    return () => window.removeEventListener('resize', targetSizeHandle)
  }, [containerEl, targetSizeHandle])

  return (
    <S.Wrapper
      ref={containerEl}
      onMouseOver={onMouseOverHandle}
      onMouseLeave={onMouseLeaveHandle}
      onClick={onClick}
      {...props}
    >
      <S.Target
        aria-expanded={active}
        role="button"
        tabIndex={0}
        aria-describedby={id}
      >
        {!target ? (
          <S.Icon
            iconSize={iconSize}
            src={helpCircleIconImg}
            alt={dict.tooltipIconAlt}
            tabIndex={-1}
            aria-hidden
          />
        ) : (
          target
        )}
      </S.Target>
      <S.Content
        placement={placement}
        isVisible={active}
        targetSize={targetSize}
        id={id}
        role="tooltip"
        aria-hidden={!active}
        dark={dark}
        contentSize={contentSize ?? 'normal'}
      >
        {children}
      </S.Content>
    </S.Wrapper>
  )
}

export default Tooltip
